import React, { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ArrowsRightLeftIcon, HandThumbUpIcon, HandThumbDownIcon, UserIcon } from '@heroicons/react/24/outline';
import { EmotionAnalysis } from '../../types';
import apiService from '../../services/api';
import UserEmotionQuestionnaire from './UserEmotionQuestionnaire';

interface EmotionAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  emotionAnalysis: EmotionAnalysis | null;
  isLoading: boolean;
  speaker: string;
  text: string;
  caseId?: string;
  turnIndex?: number;
}

const EmotionAnalysisModal: React.FC<EmotionAnalysisModalProps> = ({
  isOpen,
  onClose,
  emotionAnalysis,
  isLoading,
  speaker,
  text,
  caseId,
  turnIndex,
}) => {
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonData, setComparisonData] = useState<any>(null);
  const [isComparing, setIsComparing] = useState(false);
  const [votes, setVotes] = useState<{ [emotion: string]: { vote: string; aiScore: number } }>({});
  const [isVoting, setIsVoting] = useState(false);
  const [userEmotions, setUserEmotions] = useState<{ gratitude: number; sadness: number; anger: number; fear: number } | null>(null);
  const [showUserQuestionnaire, setShowUserQuestionnaire] = useState(false);
  const [hasUserReport, setHasUserReport] = useState(false);

  // Clear all state when component mounts or text changes
  useEffect(() => {
    console.log('EmotionAnalysisModal: Component mounted/remounted, clearing all state', { text, caseId, turnIndex });
    setVotes({});
    setUserEmotions(null);
    setHasUserReport(false);
    setShowUserQuestionnaire(false);
    setShowComparison(false);
    setComparisonData(null);
  }, [text, caseId, turnIndex]);
  const getEmotionColor = (value: number) => {
    if (value === 0) return 'bg-gray-100 text-gray-600';
    if (value === 1) return 'bg-red-100 text-red-800';
    if (value === 2) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  const getEmotionIntensity = (value: number) => {
    if (value === 0) return 'No Intensity';
    if (value === 1) return 'Low';
    if (value === 2) return 'Medium';
    return 'High';
  };

  // Load existing votes and user emotion reports when modal opens
  useEffect(() => {
    if (isOpen && caseId && turnIndex !== undefined) {
      console.log('EmotionAnalysisModal: Modal opened, clearing state and loading data', { text, caseId, turnIndex });
      
      // Clear all state when modal opens
      setVotes({});
      setUserEmotions(null);
      setHasUserReport(false);
      setShowUserQuestionnaire(false);
      setShowComparison(false);
      setComparisonData(null);
      
      // Force a small delay to ensure state is cleared before loading
      setTimeout(() => {
        console.log('EmotionAnalysisModal: Loading fresh data after modal open');
        loadVotes();
        loadUserEmotionReport();
      }, 100);
    }
  }, [isOpen, caseId, turnIndex]);

  // Reload votes and user emotion reports when text changes
  useEffect(() => {
    if (isOpen && caseId && turnIndex !== undefined) {
      console.log('EmotionAnalysisModal: Text changed, clearing state and reloading data', { text, caseId, turnIndex });
      
      // Aggressively clear all state when text changes
      setVotes({});
      setUserEmotions(null);
      setHasUserReport(false);
      setShowUserQuestionnaire(false);
      setShowComparison(false);
      setComparisonData(null);
      
      // Force a small delay to ensure state is cleared before loading
      setTimeout(() => {
        console.log('EmotionAnalysisModal: Loading fresh data after state clear');
        loadVotes();
        loadUserEmotionReport();
      }, 50);
    }
  }, [text, isOpen, caseId, turnIndex]);

  const loadVotes = async () => {
    if (!caseId || turnIndex === undefined) return;
    
    try {
      console.log('EmotionAnalysisModal: Loading votes for', { caseId, turnIndex, text });
      const response = await apiService.getEmotionVotes(caseId, turnIndex);
      console.log('EmotionAnalysisModal: Votes response', response);
      if (response.success) {
        setVotes(response.votes);
      }
    } catch (error) {
      console.error('Failed to load votes:', error);
    }
  };

  const loadUserEmotionReport = async () => {
    if (!caseId || turnIndex === undefined) return;
    
    try {
      console.log('EmotionAnalysisModal: Loading user emotion report for', { caseId, turnIndex, text });
      const response = await apiService.getUserEmotionReport(caseId, turnIndex);
      console.log('EmotionAnalysisModal: User emotion report response', response);
      if (response.success && response.userReport) {
        setUserEmotions(response.userReport.userEmotions);
        setHasUserReport(true);
      } else {
        setUserEmotions(null);
        setHasUserReport(false);
      }
    } catch (error) {
      console.error('Failed to load user emotion report:', error);
      setUserEmotions(null);
      setHasUserReport(false);
    }
  };

  const handleUserEmotionSubmit = (userEmotions: { gratitude: number; sadness: number; anger: number; fear: number }) => {
    setUserEmotions(userEmotions);
    setHasUserReport(true);
    setShowUserQuestionnaire(false);
  };

  const handleVote = async (emotion: string, vote: 'up' | 'down' | 'neutral') => {
    if (!caseId || turnIndex === undefined || !emotionAnalysis) return;
    
    setIsVoting(true);
    try {
      const response = await apiService.voteOnEmotion({
        caseId,
        turnIndex,
        emotion,
        vote,
        aiScore: emotionAnalysis.emotions[emotion as keyof typeof emotionAnalysis.emotions]
      });
      
      if (response.success) {
        // Update local votes state
        setVotes(prev => ({
          ...prev,
          [emotion]: {
            vote: vote,
            aiScore: emotionAnalysis.emotions[emotion as keyof typeof emotionAnalysis.emotions]
          }
        }));
      }
    } catch (error) {
      console.error('Failed to vote:', error);
    } finally {
      setIsVoting(false);
    }
  };

  const getVoteButtonColor = (emotion: string, voteType: 'up' | 'down') => {
    const currentVote = votes[emotion]?.vote;
    if (currentVote === voteType) {
      return voteType === 'up' ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100';
    }
    return 'text-gray-400 hover:text-gray-600';
  };

  const handleCompareAnalysis = async () => {
    if (!text.trim() || !emotionAnalysis) return;
    
    setIsComparing(true);
    try {
      // Only call API once to get analysis without dataset
      const withoutDatasetResult = await apiService.analyzeEmotionDirect(speaker, text, 'Medical Consultation', false);
      
      // Use the existing emotionAnalysis (with dataset) and the new result (without dataset)
      const comparisonResult = {
        withDataset: {
          emotions: emotionAnalysis.emotions,
          assessment: emotionAnalysis.assessment,
          reasoning: emotionAnalysis.reasoning
        },
        withoutDataset: {
          emotions: withoutDatasetResult.emotions,
          assessment: withoutDatasetResult.assessment,
          reasoning: withoutDatasetResult.reasoning
        },
        differences: {
          gratitude: emotionAnalysis.emotions.gratitude - withoutDatasetResult.emotions.gratitude,
          sadness: emotionAnalysis.emotions.sadness - withoutDatasetResult.emotions.sadness,
          anger: emotionAnalysis.emotions.anger - withoutDatasetResult.emotions.anger,
          fear: emotionAnalysis.emotions.fear - withoutDatasetResult.emotions.fear
        }
      };
      
      setComparisonData(comparisonResult);
      setShowComparison(true);
    } catch (error) {
      console.error('Error comparing emotion analysis:', error);
    } finally {
      setIsComparing(false);
    }
  };

  const getDiffColor = (diff: number) => {
    if (diff === 0) return 'text-gray-600';
    if (diff > 0) return 'text-green-600';
    return 'text-red-600';
  };

  const getDiffIcon = (diff: number) => {
    if (diff === 0) return '→';
    if (diff > 0) return '↑';
    return '↓';
  };

  return (
    <Dialog as="div" className="relative z-50" open={isOpen} onClose={onClose}>
      <Transition
        show={isOpen}
        enter="transition duration-300 ease-out"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition duration-200 ease-in"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      </Transition>

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Transition
          show={isOpen}
          enter="transition duration-300 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition duration-200 ease-in"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel className="w-full max-w-4xl max-h-[90vh] transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <Dialog.Title as="h3" className="text-xl font-semibold text-gray-900">
                Emotion Analysis
              </Dialog.Title>
              <div className="flex items-center space-x-3">
                {emotionAnalysis && !isLoading && (
                  <button
                    onClick={handleCompareAnalysis}
                    disabled={isComparing}
                    className="flex items-center space-x-2 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                  >
                    <ArrowsRightLeftIcon className="h-4 w-4" />
                    <span>{isComparing ? 'Comparing...' : 'Compare'}</span>
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

          <div className="p-6 overflow-y-auto flex-1">
            {/* Speaker and Text Context */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Speaker</h3>
              <p className="text-lg font-semibold text-gray-900 mb-3">{speaker}</p>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Text</h3>
              <p className="text-gray-800 italic">"{text}"</p>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Analyzing emotions...</span>
              </div>
            ) : emotionAnalysis ? (
              <div className="space-y-6">
                {/* Overall Assessment */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">Overall Assessment</h3>
                  <p className="text-blue-800">{emotionAnalysis.assessment}</p>
                </div>

                {/* User Questionnaire Button */}
                <div className="mb-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Emotion Analysis</h3>
                    <button
                      onClick={() => setShowUserQuestionnaire(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    >
                      <UserIcon className="h-4 w-4 mr-2" />
                      {hasUserReport ? 'Update My Ratings' : 'Rate My Emotions'}
                    </button>
                  </div>
                </div>

                {/* Core Emotions */}
                <div>
                  <div className="grid grid-cols-2 gap-4">
                    {emotionAnalysis?.emotions && Object.entries(emotionAnalysis.emotions).map(([emotion, value]) => {
                      const userValue = userEmotions?.[emotion as keyof typeof userEmotions];
                      return (
                        <div key={emotion} className="p-4 bg-white border border-gray-200 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-gray-700 capitalize">
                              {emotion.replace(/([A-Z])/g, ' $1').trim()}
                            </span>
                            <div className="flex space-x-2">
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getEmotionColor(value)}`}>
                                AI: {value}/3
                              </span>
                              {userValue !== undefined && (
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getEmotionColor(userValue)}`}>
                                  You: {userValue}/3
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* AI Analysis Bar */}
                          <div className="mb-2">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-gray-500">AI Analysis</span>
                              <span className="text-xs text-gray-500">{getEmotionIntensity(value)} Intensity</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3">
                              <div 
                                className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                                style={{ width: `${(value / 3) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                          
                          {/* User Rating Bar (if available) */}
                          {userValue !== undefined && (
                            <div className="mt-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-gray-500">Your Rating</span>
                                <span className="text-xs text-gray-500">{getEmotionIntensity(userValue)} Intensity</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-3">
                                <div 
                                  className="bg-purple-600 h-3 rounded-full transition-all duration-300"
                                  style={{ width: `${(userValue / 3) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          )}
                          
                          {/* Comparison */}
                          {userValue !== undefined && (
                            <div className="mt-2 text-xs text-gray-500">
                              {value === userValue ? (
                                <span className="text-green-600">✓ AI and you agree</span>
                              ) : (
                                <span className="text-orange-600">
                                  Δ Difference: {Math.abs(value - userValue)} point{Math.abs(value - userValue) !== 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          )}
                          
                          {/* Voting buttons */}
                          {caseId && turnIndex !== undefined && (
                            <div className="flex items-center justify-center gap-2 mt-3 pt-3 border-t border-gray-100">
                              <span className="text-xs text-gray-500 mr-2">Do you agree?</span>
                              <button
                                onClick={() => handleVote(emotion, 'up')}
                                disabled={isVoting}
                                className={`p-1 rounded-full transition-colors ${getVoteButtonColor(emotion, 'up')}`}
                                title="Agree with this emotion score"
                              >
                                <HandThumbUpIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleVote(emotion, 'down')}
                                disabled={isVoting}
                                className={`p-1 rounded-full transition-colors ${getVoteButtonColor(emotion, 'down')}`}
                                title="Disagree with this emotion score"
                              >
                                <HandThumbDownIcon className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Comparison Results */}
                {showComparison && comparisonData && (
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Dataset Comparison</h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* With Dataset */}
                      <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="text-md font-semibold text-green-900 mb-3">With Dataset Reference</h4>
                        <div className="space-y-3">
                          {comparisonData?.withDataset?.emotions && Object.entries(comparisonData.withDataset.emotions).map(([emotion, value]) => {
                            const numValue = value as number;
                            return (
                              <div key={emotion} className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700 capitalize w-16">
                                  {emotion}
                                </span>
                                <div className="flex items-center space-x-2 flex-1">
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                                      style={{ width: `${(numValue / 3) * 100}%` }}
                                    ></div>
                                  </div>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEmotionColor(numValue)}`}>
                                    {numValue}/3
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Without Dataset */}
                      <div className="bg-orange-50 p-4 rounded-lg">
                        <h4 className="text-md font-semibold text-orange-900 mb-3">Without Dataset Reference</h4>
                        <div className="space-y-3">
                          {comparisonData?.withoutDataset?.emotions && Object.entries(comparisonData.withoutDataset.emotions).map(([emotion, value]) => {
                            const numValue = value as number;
                            return (
                              <div key={emotion} className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700 capitalize w-16">
                                  {emotion}
                                </span>
                                <div className="flex items-center space-x-2 flex-1">
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                      className="bg-orange-600 h-2 rounded-full transition-all duration-300"
                                      style={{ width: `${(numValue / 3) * 100}%` }}
                                    ></div>
                                  </div>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEmotionColor(numValue)}`}>
                                    {numValue}/3
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Differences */}
                    <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                      <h4 className="text-md font-semibold text-gray-900 mb-3">Differences (With Dataset - Without Dataset)</h4>
                      <div className="grid grid-cols-4 gap-4">
                        {comparisonData?.differences && Object.entries(comparisonData.differences).map(([emotion, diff]) => {
                          const numDiff = diff as number;
                          return (
                            <div key={emotion} className="text-center">
                              <span className="text-sm font-medium text-gray-700 capitalize">{emotion}</span>
                              <div className={`text-lg font-bold ${getDiffColor(numDiff)}`}>
                                {getDiffIcon(numDiff)} {Math.abs(numDiff)}
                              </div>
                              <div className="text-xs text-gray-500">
                                {numDiff === 0 ? 'No change' : numDiff > 0 ? 'Higher with dataset' : 'Lower with dataset'}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Overall Metrics */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Overall Metrics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {emotionAnalysis?.overall && Object.entries(emotionAnalysis.overall).map(([metric, value]) => (
                      <div key={metric} className="p-4 bg-white border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700 capitalize">
                            {metric.replace(/([A-Z])/g, ' $1').trim()}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getEmotionColor(value)}`}>
                            {value}/3
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className="bg-purple-600 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${(value / 3) * 100}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{getEmotionIntensity(value)}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Reasoning */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Analysis Reasoning</h3>
                  <p className="text-gray-700">{emotionAnalysis.reasoning}</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">No emotion analysis available</p>
              </div>
            )}
          </div>

          <div className="flex justify-end p-6 border-t border-gray-200 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
          </Dialog.Panel>
        </Transition>
      </div>
      
      {/* User Emotion Questionnaire Modal */}
      <UserEmotionQuestionnaire
        key={`${caseId}-${turnIndex}-${text}`} // Force remount when text changes
        isOpen={showUserQuestionnaire}
        onClose={() => setShowUserQuestionnaire(false)}
        onSubmit={handleUserEmotionSubmit}
        speaker={speaker}
        text={text}
        caseId={caseId}
        turnIndex={turnIndex}
        existingReport={userEmotions}
      />
    </Dialog>
  );
};

export default EmotionAnalysisModal;
